import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  People,
  AccessTime,
  Business,
  PersonAdd,
  LocationCity,
  EventAvailable,
  EventBusy,
  ArrowForward,
  CalendarMonth,
  Cake,
  Celebration,
  EmojiPeople,
} from '@mui/icons-material';
import { RootState } from '../store/store';
import { dashboardApi, DashboardCelebrationsData, DashboardData } from '../api/dashboardApi';

import WebPunchCard from '../components/Dashboard/WebPunchCard';
import { attendanceApi } from '../api/attendanceApi';
import { employeeApi } from '../api/employeeApi';
import AttendanceStatusList from './AttendanceStatusList';
import { useLanguage } from '../i18n/LocalizationContext';

const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180;

  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
};

const describePieSlice = (
  centerX: number,
  centerY: number,
  radius: number,
  startAngle: number,
  endAngle: number
) => {
  const start = polarToCartesian(centerX, centerY, radius, endAngle);
  const end = polarToCartesian(centerX, centerY, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;

  return [
    `M ${centerX} ${centerY}`,
    `L ${start.x} ${start.y}`,
    `A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`,
    'Z',
  ].join(' ');
};

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const [data, setData] = useState<DashboardData | null>(null);
  const [celebrations, setCelebrations] = useState<DashboardCelebrationsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showBiometricBanner, setShowBiometricBanner] = useState(false);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);
        const [dashboardResult, celebrationsResult] = await Promise.all([
          dashboardApi.getData(),
          dashboardApi.getCelebrations(),
        ]);
        setData(dashboardResult);
        setCelebrations(celebrationsResult);
        setError(null);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'dashboard.loadFailed');
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  useEffect(() => {
    const checkBiometricStatus = async () => {
      try {
        const emp = await employeeApi.getCurrentEmployee();
        const status = await attendanceApi.getTodayStatus(emp.id);
        const biometricCheckIn = status.todayPunches.some(
          (punch: any) =>
            String(punch?.type || '').toUpperCase() === 'CHECK IN'
            && String(punch?.modeOfEntry || '').toLowerCase().includes('biometric')
        );
        setShowBiometricBanner(biometricCheckIn);
      } catch {
        setShowBiometricBanner(false);
      }
    };

    void checkBiometricStatus();
  }, []);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('dashboard.goodMorning');
    if (hour < 17) return t('dashboard.goodAfternoon');
    return t('dashboard.goodEvening');
  };

  const dateLocale = language === 'hi' ? 'hi-IN' : language === 'mr' ? 'mr-IN' : 'en-IN';

  const [attendanceModal, setAttendanceModal] = useState<{ open: boolean; status: 'present' | 'absent' | null; date: string }>({ open: false, status: null, date: '' });

  const statCards = [
    {
      title: t('dashboard.totalEmployees'),
      value: data?.totalEmployees ?? 0,
      icon: <People sx={{ fontSize: 32 }} />,
      color: '#667eea',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      onClick: () => navigate('/employees'),
    },
    {
      title: t('dashboard.presentToday'),
      value: data?.presentToday ?? 0,
      icon: <EventAvailable sx={{ fontSize: 32 }} />,
      color: '#4caf50',
      gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      onClick: () => {
        const today = new Date().toISOString().slice(0, 10);
        setAttendanceModal({ open: true, status: 'present', date: today });
      },
    },
    {
      title: t('dashboard.absentToday'),
      value: data?.absentToday ?? 0,
      icon: <EventBusy sx={{ fontSize: 32 }} />,
      color: '#f44336',
      gradient: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
      onClick: () => {
        const today = new Date().toISOString().slice(0, 10);
        setAttendanceModal({ open: true, status: 'absent', date: today });
      },
    },
    {
      title: t('departments'),
      value: data?.totalDepartments ?? 0,
      icon: <Business sx={{ fontSize: 32 }} />,
      color: '#ff9800',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      onClick: () => navigate('/settings/account-management/departments'),
    },
    {
      title: t('branches'),
      value: data?.totalBranches ?? 0,
      icon: <LocationCity sx={{ fontSize: 32 }} />,
      color: '#9c27b0',
      gradient: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
      onClick: () => navigate('/settings/account-management/branches'),
    },
    {
      title: t('dashboard.newJoinees'),
      value: data?.newJoineesThisMonth ?? 0,
      icon: <PersonAdd sx={{ fontSize: 32 }} />,
      color: '#00bcd4',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      onClick: () => navigate('/employees?status=newjoinee'),
    },
  ];

  const maxDeptCount = data?.departmentStats?.length
    ? Math.max(...data.departmentStats.map((d) => d.employeeCount))
    : 1;
  const attendanceOverviewTotal = (data?.presentToday ?? 0) + (data?.absentToday ?? 0);
  const presentAttendanceRate = attendanceOverviewTotal > 0
    ? Number((((data?.presentToday ?? 0) / attendanceOverviewTotal) * 100).toFixed(1))
    : 0;
  const absentAttendanceRate = attendanceOverviewTotal > 0
    ? Number((100 - presentAttendanceRate).toFixed(1))
    : 0;
  const presentAttendanceDegrees = Math.max(0, Math.min(presentAttendanceRate, 100)) * 3.6;
  const presentSlicePath = describePieSlice(60, 60, 56, 0, presentAttendanceDegrees);
  const absentSlicePath = describePieSlice(60, 60, 56, presentAttendanceDegrees, 360);
  const presentTooltip = t('dashboard.presentTooltip', {
    count: data?.presentToday ?? 0,
    percent: presentAttendanceRate,
  });
  const absentTooltip = t('dashboard.absentTooltip', {
    count: data?.absentToday ?? 0,
    percent: absentAttendanceRate,
  });
  const celebrationCardSx = {
    p: 3,
    borderRadius: 3,
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    height: 320,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  } as const;

  const renderCelebrationList = (
    items: Array<{
      employeeId: string;
      fullName: string;
      employeeCode: string;
      departmentName?: string | null;
      designationName?: string | null;
      profileImagePath?: string | null;
    }>,
    emptyMessage: string,
    meta?: (item: any) => string
  ) => {
    if (!items.length) {
      return (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">{emptyMessage}</Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          pr: 1,
          scrollbarGutter: 'stable',
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 3 },
        }}
      >
      <Stack spacing={1.5}>
        {items.map((item) => (
          <Box
            key={item.employeeId}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1.5,
              borderRadius: 2,
              bgcolor: '#f8fafc',
            }}
          >
            <Avatar src={item.profileImagePath || undefined} sx={{ bgcolor: '#667eea' }}>
              {item.fullName.split(' ').map((part) => part[0]).join('').slice(0, 2).toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" fontWeight={700} noWrap>
                {item.fullName}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {item.employeeCode}
                {item.designationName ? ` • ${t(item.designationName)}` : ''}
                {item.departmentName ? ` • ${t(item.departmentName)}` : ''}
              </Typography>
              {meta && (
                <Typography variant="caption" sx={{ display: 'block', color: 'primary.main', mt: 0.25 }}>
                  {meta(item)}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Stack>
      </Box>
    );
  };

  const { user } = useSelector((state: RootState) => state.auth);
  // Determine if user is only admin (must be after user is defined)
  const isAdminOnly = !!(user && (user.roleName === 'Admin' || (Array.isArray(user.roles) && user.roles.length === 1 && user.roles[0] === 'Admin')));

  return (
    <>
      <Box>
      {/* Header */}
      <Box sx={{ mb: { xs: 2, md: 4 } }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}>
          {getGreeting()}, {user?.fullName?.split(' ')[0]}!
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' } }}>
          {t('dashboard.organizationToday')}
        </Typography>
      </Box>

      {showBiometricBanner && (
        <Alert severity="info" sx={{ mb: 3 }} onClose={() => setShowBiometricBanner(false)}>
          {t('dashboard.biometricSignedIn')}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {t(error)}
        </Alert>
      )}

      {/* Stat Cards */}
      <Grid container spacing={2.5} sx={{ mb: 4, alignItems: 'stretch' }}>
        {/* Web Punch Card */}
        {!isAdminOnly && (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <WebPunchCard />
          </Grid>
        )}

        {/* Stat Cards Grid */}
        <Grid size={{ xs: 12, sm: isAdminOnly ? 12 : 6, md: isAdminOnly ? 12 : 8, lg: isAdminOnly ? 12 : 9 }}>
          <Grid container spacing={2.5} sx={{ height: '100%' }}>
            {statCards.map((card, index) => (
              <Grid size={{ xs: 6, sm: 4, md: 4 }} key={index}>
                {loading ? (
                  <Skeleton variant="rounded" height={120} sx={{ borderRadius: 3 }} />
                ) : (
                  <Card
                    sx={{
                      borderRadius: 3,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      height: '100%',
                      cursor: 'pointer',
                      overflow: 'hidden',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: `0 8px 25px ${card.color}25`,
                      },
                    }}
                    onClick={card.onClick}
                  >
                    <Box sx={{ height: 3, background: card.gradient }} />
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 }, display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
                      <Box
                        sx={{
                          background: `${card.color}14`,
                          borderRadius: 2.5,
                          minWidth: { xs: 36, sm: 48 },
                          height: { xs: 36, sm: 48 },
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: card.color,
                          '& .MuiSvgIcon-root': { fontSize: { xs: 22, sm: 32 } },
                        }}
                      >
                        {card.icon}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography fontWeight={800} lineHeight={1.1} sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' } }}>
                          {card.value}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontWeight={500} noWrap sx={{ mt: 0.25, display: 'block', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                          {card.title}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>

      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: { xs: 2, md: 4 } }}>
        <Grid size={{ xs: 12, md: 4 }}>
          {loading ? (
            <Skeleton variant="rounded" height={320} />
          ) : (
            <Paper sx={celebrationCardSx}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexShrink: 0 }}>
                <Cake color="secondary" />
                <Typography variant="h6" fontWeight="bold">{t('dashboard.todaysBirthdays')}</Typography>
                <Chip size="small" label={celebrations?.birthdays.length ?? 0} />
              </Box>
              {renderCelebrationList(
                celebrations?.birthdays ?? [],
                t('dashboard.noBirthdaysToday'),
                (item) => t('dashboard.dobWithDate', {
                  date: new Date(item.dateOfBirth).toLocaleDateString(dateLocale, { day: '2-digit', month: 'short' }),
                })
              )}
            </Paper>
          )}
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          {loading ? (
            <Skeleton variant="rounded" height={320} />
          ) : (
            <Paper sx={celebrationCardSx}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexShrink: 0 }}>
                <Celebration color="primary" />
                <Typography variant="h6" fontWeight="bold">{t('dashboard.workAnniversaries')}</Typography>
                <Chip size="small" label={celebrations?.anniversaries.length ?? 0} />
              </Box>
              {renderCelebrationList(
                celebrations?.anniversaries ?? [],
                t('dashboard.noWorkAnniversariesToday'),
                (item) => t('dashboard.yearsCompleted', { count: item.yearsCompleted })
              )}
            </Paper>
          )}
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          {loading ? (
            <Skeleton variant="rounded" height={320} />
          ) : (
            <Paper sx={celebrationCardSx}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexShrink: 0 }}>
                <EmojiPeople sx={{ color: '#00bcd4' }} />
                <Typography variant="h6" fontWeight="bold">{t('dashboard.newJoiners')}</Typography>
                <Chip size="small" label={celebrations?.newJoiners.length ?? 0} />
              </Box>
              {renderCelebrationList(
                celebrations?.newJoiners ?? [],
                t('dashboard.noNewJoinersLast7Days'),
                (item) => t('dashboard.joinedWithDate', {
                  date: new Date(item.joinedDate).toLocaleDateString(dateLocale, { day: '2-digit', month: 'short', year: 'numeric' }),
                })
              )}
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Attendance Rate + Department Stats */}
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: { xs: 2, md: 4 } }}>
        {/* Attendance Overview */}
        <Grid size={{ xs: 12, md: 5 }}>
          {loading ? (
            <Skeleton variant="rounded" height={320} />
          ) : (
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight="bold">
                  {t('dashboard.attendanceOverview')}
                </Typography>
                <Chip
                  icon={<CalendarMonth sx={{ fontSize: 16 }} />}
                  label={t('common.today')}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>

              {/* Attendance Rate Circle */}
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    width: 140,
                    height: 140,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.45), 0 8px 24px rgba(0,0,0,0.12)',
                  }}
                >
                  {attendanceOverviewTotal > 0 ? (
                    <Box
                      component="svg"
                      viewBox="0 0 120 120"
                      role="img"
                      aria-label={t('dashboard.attendanceOverviewChart')}
                      sx={{ width: 140, height: 140, display: 'block' }}
                    >
                      {presentAttendanceRate > 0 && (
                        presentAttendanceRate === 100 ? (
                          <circle cx="60" cy="60" r="56" fill="#2e7d32" style={{ cursor: 'default' }}>
                            <title>{presentTooltip}</title>
                          </circle>
                        ) : (
                          <path d={presentSlicePath} fill="#2e7d32" stroke="#fff" strokeWidth="1.5" style={{ cursor: 'default' }}>
                            <title>{presentTooltip}</title>
                          </path>
                        )
                      )}
                      {absentAttendanceRate > 0 && (
                        absentAttendanceRate === 100 ? (
                          <circle cx="60" cy="60" r="56" fill="#d32f2f" style={{ cursor: 'default' }}>
                            <title>{absentTooltip}</title>
                          </circle>
                        ) : (
                          <path d={absentSlicePath} fill="#d32f2f" stroke="#fff" strokeWidth="1.5" style={{ cursor: 'default' }}>
                            <title>{absentTooltip}</title>
                          </path>
                        )
                      )}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        width: 140,
                        height: 140,
                        borderRadius: '50%',
                        bgcolor: '#f0f0f0',
                      }}
                    />
                  )}
                </Box>
              </Box>

              {/* Attendance Stats Row */}
              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight="bold" color="success.main">
                      {data?.presentToday ?? 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('dashboard.presentPercent', { percent: presentAttendanceRate })}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight="bold" color="error.main">
                      {data?.absentToday ?? 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('dashboard.absentPercent', { percent: absentAttendanceRate })}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          )}
        </Grid>

        {/* Department Distribution */}
        <Grid size={{ xs: 12, md: 7 }}>
          {loading ? (
            <Skeleton variant="rounded" height={320} />
          ) : (
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, md: 3 } }}>
                <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
                  {t('dashboard.departmentDistribution')}
                </Typography>
                <Chip
                  label={t(
                    (data?.totalDepartments ?? 0) === 1
                      ? 'dashboard.departmentCountShortOne'
                      : 'dashboard.departmentCountShort',
                    { count: data?.totalDepartments ?? 0 }
                  )}
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
              </Box>

              {data?.departmentStats && data.departmentStats.length > 0 ? (
                data.departmentStats.map((dept, index) => {
                  const colors = ['#667eea', '#4caf50', '#ff9800', '#f44336', '#9c27b0', '#00bcd4'];
                  return (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {t(dept.departmentName)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t(
                            dept.employeeCount === 1 ? 'dashboard.employeeCountOne' : 'dashboard.employeeCount',
                            { count: dept.employeeCount }
                          )}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={(dept.employeeCount / maxDeptCount) * 100}
                        sx={{
                          height: 10,
                          borderRadius: 5,
                          bgcolor: '#f0f0f0',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 5,
                            background: `linear-gradient(90deg, ${colors[index % colors.length]}, ${colors[index % colors.length]}99)`,
                          },
                        }}
                      />
                    </Box>
                  );
                })
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Business sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary">
                    {t('dashboard.noDepartmentData')}
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Weekly Attendance Trend + Recent Employees */}
      <Grid container spacing={{ xs: 2, md: 3 }}>
        {/* Weekly Trend */}
        <Grid size={{ xs: 12, md: 5 }}>
          {loading ? (
            <Skeleton variant="rounded" height={350} />
          ) : (
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                {t('dashboard.weeklyAttendance')}
              </Typography>
              {data?.weeklyAttendance && data.weeklyAttendance.length > 0 ? (
                <Box sx={{ mt: 2 }}>
                  {data.weeklyAttendance.map((day, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Typography variant="body2" sx={{ width: 40, fontWeight: 500 }}>
                        {day.day}
                      </Typography>
                      <Box sx={{ flex: 1, mx: 1.5 }}>
                        <Box sx={{ display: 'flex', height: 24, borderRadius: 2, overflow: 'hidden', bgcolor: '#f5f5f5' }}>
                          <Box
                            sx={{
                              width: day.total > 0 ? `${(day.present / day.total) * 100}%` : 0,
                              bgcolor: '#4caf50',
                              borderRadius: '4px 0 0 4px',
                              transition: 'width 0.5s ease',
                            }}
                          />
                          <Box
                            sx={{
                              width: day.total > 0 ? `${(day.absent / day.total) * 100}%` : 0,
                              bgcolor: '#ff6b6b',
                              borderRadius: '0 4px 4px 0',
                            }}
                          />
                        </Box>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ width: 32, textAlign: 'right' }}>
                        {day.present}/{day.total}
                      </Typography>
                    </Box>
                  ))}
                  <Box sx={{ display: 'flex', gap: 3, mt: 2, justifyContent: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: '#4caf50' }} />
                      <Typography variant="caption">{t('attendance.present')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: '#ff6b6b' }} />
                      <Typography variant="caption">{t('attendance.absent')}</Typography>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <AccessTime sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary">{t('dashboard.noAttendanceDataThisWeek')}</Typography>
                </Box>
              )}
            </Paper>
          )}
        </Grid>

        {/* Recent Employees */}
        <Grid size={{ xs: 12, md: 7 }}>
          {loading ? (
            <Skeleton variant="rounded" height={350} />
          ) : (
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="bold">
                  {t('dashboard.recentEmployees')}
                </Typography>
                <Tooltip title={t('dashboard.viewAllEmployees')}>
                  <IconButton size="small" color="primary" onClick={() => navigate('/employees')}>
                    <ArrowForward />
                  </IconButton>
                </Tooltip>
              </Box>

              {data?.recentEmployees && data.recentEmployees.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>{t('employee.employee')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{t('employee.department')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{t('employee.designation')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{t('dashboard.joined')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.recentEmployees.map((emp) => (
                        <TableRow key={emp.id} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar
                                sx={{
                                  width: 36,
                                  height: 36,
                                  fontSize: 14,
                                  bgcolor: '#667eea',
                                }}
                              >
                                {emp.fullName.split(' ').map((n) => n[0]).join('').toUpperCase()}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  {emp.fullName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {emp.email}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip label={t(emp.department)} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{emp.designation}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {new Date(emp.dateOfJoining).toLocaleDateString(dateLocale, {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                              })}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <People sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary">
                    {t('dashboard.noEmployeesAddedYet')}
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
        </Grid>
      </Grid>
      </Box>
      {/* Attendance Modal */}
      <AttendanceStatusList
        open={attendanceModal.open}
        status={attendanceModal.status || 'present'}
        date={attendanceModal.date}
        onClose={() => setAttendanceModal({ open: false, status: null, date: '' })}
      />
    </>
  );
};

export default DashboardPage;
